import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import axios from 'axios';
import { motion } from 'framer-motion';
import Skeleton from '../ui/Skeleton';
import PricingSection from '../PricingSection';
import { Empty, PageHeader } from '../ui/kit';
import { StripeCheckout } from '../StripeCheckout';
import { MONTHLY, PLAN_NAMES, PLAN_ORDER, type PlanId } from '@foundlio/pricing';

type PlanType = PlanId;

interface Subscription {
  id: string;
  planType: string;
  status: 'active' | 'trialing' | 'canceled' | 'canceling' | 'past_due';
  currentPeriodEnd: string;
  amount: number;
}

interface BillingHistoryItem {
  id: string;
  amount: number;
  currency: string;
  status: string;
  created: string;
  invoiceUrl: string;
  invoicePdf: string;
  description: string;
  periodStart: string | null;
  periodEnd: string | null;
}

// Icon Components
const CreditCardIcon: React.FC<{ className?: string }> = ({ className = "w-6 h-6" }) => (
  <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
    <rect x="2" y="5" width="20" height="14" rx="2" strokeLinecap="round" strokeLinejoin="round" />
    <path strokeLinecap="round" strokeLinejoin="round" d="M2 10h20" />
    <path strokeLinecap="round" strokeLinejoin="round" d="M6 14h2M12 14h2" />
  </svg>
);

const CalendarIcon: React.FC<{ className?: string }> = ({ className = "w-4 h-4" }) => (
  <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M6.75 3v2.25M17.25 3v2.25M3 18.75V7.5a2.25 2.25 0 0 1 2.25-2.25h13.5A2.25 2.25 0 0 1 21 7.5v11.25m-18 0A2.25 2.25 0 0 0 5.25 21h13.5A2.25 2.25 0 0 0 21 18.75m-18 0v-7.5A2.25 2.25 0 0 1 5.25 9h13.5A2.25 2.25 0 0 1 21 11.25v7.5" />
  </svg>
);

const SettingsIcon: React.FC<{ className?: string }> = ({ className = "w-4 h-4" }) => (
  <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M9.594 3.94c.09-.542.56-.94 1.11-.94h2.593c.55 0 1.02.398 1.11.94l.213 1.281c.063.374.313.686.645.87.074.04.147.083.22.127.325.196.72.257 1.075.124l1.217-.456a1.125 1.125 0 0 1 1.37.49l1.296 2.247a1.125 1.125 0 0 1-.26 1.431l-1.003.827c-.293.241-.438.613-.43.992a7.723 7.723 0 0 1 0 .255c-.008.378.137.75.43.991l1.004.827c.424.35.534.955.26 1.43l-1.298 2.247a1.125 1.125 0 0 1-1.369.491l-1.217-.456c-.355-.133-.75-.072-1.076.124a6.47 6.47 0 0 1-.22.128c-.331.183-.581.495-.644.869l-.213 1.281c-.09.543-.56.94-1.11.94h-2.594c-.55 0-1.019-.398-1.11-.94l-.212-1.281c-.062-.374-.312-.686-.644-.87a6.52 6.52 0 0 1-.22-.127c-.325-.196-.72-.257-1.076-.124l-1.217.456a1.125 1.125 0 0 1-1.369-.49l-1.297-2.247a1.125 1.125 0 0 1 .26-1.431l1.004-.827c.292-.24.437-.613.43-.991a6.932 6.932 0 0 1 0-.255c.007-.38-.138-.751-.43-.992l-1.004-.827a1.125 1.125 0 0 1-.26-1.43l1.297-2.247a1.125 1.125 0 0 1 1.37-.491l1.216.456c.356.133.751.072 1.076-.124.072-.044.146-.086.22-.128.332-.183.582-.495.644-.869l.214-1.28Z" />
    <path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 1 1-6 0 3 3 0 0 1 6 0Z" />
  </svg>
);

const TrashIcon: React.FC<{ className?: string }> = ({ className = "w-4 h-4" }) => (
  <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
    <path strokeLinecap="round" strokeLinejoin="round" d="m14.74 9-.346 9m-4.788 0L9.26 9m9.968-3.21c.342.052.682.107 1.022.166m-1.022-.165L18.16 19.673a2.25 2.25 0 0 1-2.244 2.077H8.084a2.25 2.25 0 0 1-2.244-2.077L4.772 5.79m14.456 0a48.108 48.108 0 0 0-3.478-.397m-12 .562c.34-.059.68-.114 1.022-.165m0 0a48.11 48.11 0 0 1 3.478-.397m7.5 0v-.916c0-1.18-.91-2.164-2.09-2.201a51.964 51.964 0 0 0-3.32 0c-1.18.037-2.09 1.022-2.09 2.201v.916m7.5 0a48.667 48.667 0 0 0-7.5 0" />
  </svg>
);

const DocumentIcon: React.FC<{ className?: string }> = ({ className = "w-4 h-4" }) => (
  <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 14.25v-2.625a3.375 3.375 0 0 0-3.375-3.375h-1.5A1.125 1.125 0 0 1 13.5 7.125v-1.5a3.375 3.375 0 0 0-3.375-3.375H8.25m2.25 0H5.625c-.621 0-1.125.504-1.125 1.125v17.25c0 .621.504 1.125 1.125 1.125h12.75c.621 0 1.125-.504 1.125-1.125V11.25a9 9 0 0 0-9-9Z" />
  </svg>
);

const ExclamationCircleIcon: React.FC<{ className?: string }> = ({ className = "w-5 h-5" }) => (
  <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v3.75m9-.75a9 9 0 1 1-18 0 9 9 0 0 1 18 0Zm-9 3.75h.008v.008H12v-.008Z" />
  </svg>
);

const BillingPage: React.FC = () => {
  const [showPlanPicker, setShowPlanPicker] = useState(false);
  const queryClient = useQueryClient();

  const { data: subscriptionData, isLoading: subscriptionLoading, error: subscriptionError } = useQuery({
    queryKey: ['subscription'],
    queryFn: async () => {
      try {
        const response = await axios.get('/api/payment/subscription');
        return response.data.data.subscription as Subscription;
      } catch (error: unknown) {
        if (error && typeof error === 'object' && 'response' in error) {
          const axiosError = error as { response?: { status?: number } };
          if (axiosError.response?.status === 404) return null;
        }
        throw error;
      }
    },
    retry: 1,
    refetchOnWindowFocus: false,
  });

  const { data: billingHistoryData, isLoading: billingLoading } = useQuery({
    queryKey: ['billing-history'],
    queryFn: async () => {
      try {
        const response = await axios.get('/api/payment/billing-history?limit=10');
        return response.data.data.billingHistory as BillingHistoryItem[];
      } catch (error: unknown) {
        if (error && typeof error === 'object' && 'response' in error) {
          const axiosError = error as { response?: { status?: number } };
          if (axiosError.response?.status === 404) return [];
        }
        throw error;
      }
    },
    retry: 1,
    refetchOnWindowFocus: false,
  });

  const customerPortalMutation = useMutation({
    mutationFn: async () => {
      const response = await axios.post('/api/payment/customer-portal-session');
      return response.data.data.url;
    },
    onSuccess: (url) => window.open(url, '_blank'),
    onError: (error: unknown) => {
      console.error('Failed to create customer portal session:', error);
      alert('Failed to open billing portal. Please try again.');
    },
  });

  const changePlanMutation = useMutation({
    mutationFn: async (planType: PlanType) => {
      const response = await axios.put('/api/payment/subscription/plan', { planType });
      return response.data.data.subscription;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['subscription'] });
      setShowPlanPicker(false);
      alert('Plan changed successfully!');
    },
    onError: (error: unknown) => {
      console.error('Failed to change plan:', error);
      alert('Failed to change plan. Please try again.');
    },
  });

  const cancelSubscriptionMutation = useMutation({
    mutationFn: async () => {
      const response = await axios.delete('/api/payment/subscription');
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['subscription'] });
    },
    onError: (error: unknown) => {
      console.error('Failed to cancel subscription:', error);
      alert('Failed to cancel subscription. Please try again.');
    },
  });

  const formatCurrency = (amount: number, currency: string = 'usd') =>
    new Intl.NumberFormat('en-US', { style: 'currency', currency: currency.toUpperCase() }).format(amount / 100);

  const formatDate = (dateString: string) =>
    new Date(dateString).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });

  const getStatusBadge = (status: string) => {
    const styles: Record<string, string> = {
      active: 'bg-green-100 text-green-700',
      trialing: 'bg-accent/10 text-accent',
      past_due: 'bg-zinc-200 text-zinc-800',
      canceled: 'bg-red-100 text-red-700',
      canceling: 'bg-yellow-100 text-yellow-700',
    };
    const dotStyles: Record<string, string> = {
      active: 'bg-green-500',
      trialing: 'bg-accent',
      past_due: 'bg-zinc-500',
      canceled: 'bg-red-500',
      canceling: 'bg-yellow-500',
    };
    const labels: Record<string, string> = {
      active: 'Active',
      trialing: 'Trial',
      past_due: 'Past Due',
      canceled: 'Cancelled',
      canceling: 'Cancels at period end',
    };
    const style = styles[status] || 'bg-zinc-100 text-zinc-700';
    const dot = dotStyles[status] || 'bg-zinc-500';
    return (
      <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium ${style}`}>
        <span className={`w-1.5 h-1.5 rounded-full ${dot}`} />
        {labels[status] || status}
      </span>
    );
  };

  if (subscriptionLoading) {
    return (
      <div className="space-y-8">
        <div>
          <Skeleton variant="text" width={280} height={32} className="mb-2" />
          <Skeleton variant="text" width={320} height={16} />
        </div>
        <div className="bg-white border border-zinc-200 rounded-xl overflow-hidden">
          <div className="px-6 py-5 border-b border-zinc-100">
            <Skeleton variant="text" width={180} height={20} />
          </div>
          <div className="p-6 space-y-4">
            <Skeleton variant="text" width={200} height={24} />
            <Skeleton variant="text" width={120} height={36} />
            <Skeleton variant="text" width={280} height={14} />
          </div>
        </div>
      </div>
    );
  }

  if (subscriptionError) {
    return (
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="bg-red-50 border border-red-200 rounded-xl p-6">
        <div className="flex items-start gap-4">
          <div className="w-10 h-10 bg-red-100 rounded-full flex items-center justify-center flex-shrink-0">
            <ExclamationCircleIcon className="w-5 h-5 text-red-600" />
          </div>
          <div>
            <h3 className="font-semibold text-red-900">Error loading billing information</h3>
            <p className="text-sm text-red-700 mt-1">Unable to load your billing information. Please try again later.</p>
          </div>
        </div>
      </motion.div>
    );
  }

  // No subscription — show plan picker
  if (!subscriptionLoading && subscriptionData === null) {
    return (
      <div className="space-y-8">
        <PageHeader
          eyebrow="Account"
          title="Choose Your Plan"
          description="Select a plan to get started with Foundlio."
        />

        <PricingSection
          onSelectPlan={(planType) => {
            // StripeCheckout handles the redirect
            const btn = document.getElementById(`checkout-${planType}`);
            btn?.click();
          }}
          currentPlan={null}
        />

        {/* Hidden checkout buttons for each plan */}
        <div className="hidden">
          {PLAN_ORDER.map((plan) => (
            <StripeCheckout
              key={plan}
              planType={plan}
              buttonText={`Subscribe to ${PLAN_NAMES[plan]}`}
              onError={(error) => alert(error)}
              disabled={false}
              className="hidden"
            />
          ))}
        </div>

        {/* Visible checkout buttons wired to PricingSection */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {PLAN_ORDER.map((plan) => (
            <div key={plan} id={`checkout-wrapper-${plan}`}>
              <StripeCheckout
                planType={plan}
                buttonText={`Subscribe to ${PLAN_NAMES[plan]}`}
                onError={(error) => alert(error)}
                disabled={false}
                className="w-full rounded-full bg-primary px-6 py-3 text-sm font-medium text-white transition-transform hover:scale-[1.02] active:scale-[0.99]"
              />
            </div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-8">
      {/* Header */}
      <PageHeader
        eyebrow="Account"
        title="Billing & Subscription"
        description="Manage your subscription and billing information."
      />

      {/* Current Subscription */}
      <div className="bg-white border border-zinc-200 rounded-xl overflow-hidden">
        <div className="px-6 py-5 border-b border-zinc-100">
          <h3 className="font-mono text-[11px] uppercase tracking-eyebrow text-zinc-500">Current Subscription</h3>
        </div>

        {subscriptionData ? (
          <div className="p-6">
            <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-6">
              <div className="flex-1">
                <div className="flex flex-wrap items-center gap-3 mb-3">
                  <h4 className="font-display text-xl font-bold tracking-tight text-primary">
                    {PLAN_NAMES[subscriptionData.planType as PlanType] || subscriptionData.planType} Plan
                  </h4>
                  {getStatusBadge(subscriptionData.status)}
                </div>

                <div className="mb-4">
                  <span className="font-display text-4xl font-bold tracking-[-0.03em] text-primary">
                    {formatCurrency(subscriptionData.amount)}
                  </span>
                  <span className="text-muted">/month</span>
                </div>

                <div className="flex items-center gap-2 text-sm text-muted">
                  <CalendarIcon className="w-4 h-4" />
                  Next billing: {formatDate(subscriptionData.currentPeriodEnd)}
                </div>
              </div>
            </div>

            {/* Actions */}
            <div className="flex flex-wrap gap-3 mt-6 pt-6 border-t border-zinc-100">
              <motion.button
                onClick={() => customerPortalMutation.mutate()}
                disabled={customerPortalMutation.isPending}
                whileHover={{ scale: customerPortalMutation.isPending ? 1 : 1.02 }}
                whileTap={{ scale: customerPortalMutation.isPending ? 1 : 0.98 }}
                className="inline-flex items-center gap-2 rounded-full bg-primary px-5 py-2.5 text-sm font-medium text-white transition-transform hover:scale-[1.02] active:scale-[0.99] disabled:opacity-50 disabled:hover:scale-100"
              >
                {customerPortalMutation.isPending ? (
                  <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent" />
                ) : (
                  <SettingsIcon />
                )}
                Manage Billing
              </motion.button>

              <motion.button
                onClick={() => setShowPlanPicker(!showPlanPicker)}
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                className="inline-flex items-center gap-2 rounded-full bg-white/60 px-5 py-2.5 text-sm font-medium text-primary ring-1 ring-zinc-300 transition-all hover:bg-white hover:ring-zinc-400"
              >
                Change Plan
              </motion.button>

              {subscriptionData.status !== 'canceled' && subscriptionData.status !== 'canceling' && (
                <motion.button
                  onClick={() => {
                    if (confirm('Are you sure you want to cancel? Your service continues until the end of the billing period.')) {
                      cancelSubscriptionMutation.mutate();
                    }
                  }}
                  disabled={cancelSubscriptionMutation.isPending}
                  whileHover={{ scale: cancelSubscriptionMutation.isPending ? 1 : 1.02 }}
                  whileTap={{ scale: cancelSubscriptionMutation.isPending ? 1 : 0.98 }}
                  className="inline-flex items-center gap-2 rounded-full bg-white px-5 py-2.5 text-sm font-medium text-red-700 ring-1 ring-red-600/30 transition-colors hover:bg-red-50 disabled:opacity-50"
                >
                  {cancelSubscriptionMutation.isPending ? (
                    <div className="animate-spin rounded-full h-4 w-4 border-2 border-red-400 border-t-transparent" />
                  ) : (
                    <TrashIcon />
                  )}
                  Cancel Subscription
                </motion.button>
              )}
            </div>
          </div>
        ) : (
          <div className="p-8 text-center">
            <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-paper-deep">
              <CreditCardIcon className="w-6 h-6 text-zinc-400" />
            </div>
            <h3 className="mt-4 font-medium text-primary">No active subscription</h3>
            <p className="mt-1 text-sm text-muted">You don't have an active subscription yet.</p>
          </div>
        )}
      </div>

      {/* Plan Change Picker */}
      {showPlanPicker && subscriptionData && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-white border border-zinc-200 rounded-xl overflow-hidden"
        >
          <div className="px-6 py-5 border-b border-zinc-100 flex items-center justify-between">
            <h3 className="font-mono text-[11px] uppercase tracking-eyebrow text-zinc-500">Change Plan</h3>
            <button onClick={() => setShowPlanPicker(false)} className="text-sm text-muted hover:text-primary">
              Cancel
            </button>
          </div>
          <div className="p-6">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {PLAN_ORDER.map((plan) => {
                const isCurrent = subscriptionData.planType === plan;
                return (
                  <button
                    key={plan}
                    onClick={() => {
                      if (!isCurrent && confirm(`Switch to ${PLAN_NAMES[plan]} plan ($${MONTHLY[plan]}/mo)?`)) {
                        changePlanMutation.mutate(plan);
                      }
                    }}
                    disabled={isCurrent || changePlanMutation.isPending}
                    className={`p-4 rounded-xl border-2 text-left transition-all ${
                      isCurrent
                        ? 'border-primary bg-paper-deep'
                        : 'border-zinc-200 hover:border-zinc-300'
                    } disabled:opacity-60`}
                  >
                    <div className="font-semibold text-primary">{PLAN_NAMES[plan]}</div>
                    <div className="text-2xl font-bold text-primary mt-1">${MONTHLY[plan]}<span className="text-sm font-normal text-muted">/mo</span></div>
                    {isCurrent && <span className="text-xs text-accent font-medium mt-2 block">Current plan</span>}
                  </button>
                );
              })}
            </div>
          </div>
        </motion.div>
      )}

      {/* Billing History */}
      {subscriptionData && (
        <div className="bg-white border border-zinc-200 rounded-xl overflow-hidden">
          <div className="px-6 py-5 border-b border-zinc-100">
            <h3 className="font-mono text-[11px] uppercase tracking-eyebrow text-zinc-500">Billing History</h3>
          </div>

          {billingLoading ? (
            <div className="p-6 space-y-3">
              {[1, 2, 3].map((i) => (
                <div key={i} className="flex items-center justify-between">
                  <Skeleton variant="text" width={100} height={14} />
                  <Skeleton variant="text" width={60} height={14} />
                </div>
              ))}
            </div>
          ) : billingHistoryData && billingHistoryData.length > 0 ? (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="bg-zinc-50 border-b border-zinc-100">
                    <th className="px-6 py-3 text-left text-xs font-medium text-muted uppercase tracking-wider">Date</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-muted uppercase tracking-wider">Description</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-muted uppercase tracking-wider">Amount</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-muted uppercase tracking-wider">Status</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-muted uppercase tracking-wider">Invoice</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-zinc-100">
                  {billingHistoryData.map((invoice) => (
                    <tr key={invoice.id} className="hover:bg-zinc-100 transition-colors">
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-primary">{formatDate(invoice.created)}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-primary">{invoice.description}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-primary">{formatCurrency(invoice.amount, invoice.currency)}</td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium ${
                          invoice.status === 'paid' ? 'bg-green-100 text-green-700' : invoice.status === 'open' ? 'bg-zinc-200 text-zinc-800' : 'bg-red-100 text-red-700'
                        }`}>
                          <span className={`w-1.5 h-1.5 rounded-full ${
                            invoice.status === 'paid' ? 'bg-green-500' : invoice.status === 'open' ? 'bg-zinc-500' : 'bg-red-500'
                          }`} />
                          {invoice.status}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        {invoice.invoiceUrl && (
                          <a href={invoice.invoiceUrl} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1.5 text-sm text-primary underline-offset-4 hover:underline transition-colors">
                            <DocumentIcon />
                            View
                          </a>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            /*
              No drawing here: this is a waiting state inside a card that is
              already labelled, and the section header carries the subject.
            */
            <Empty
              compact
              title="No billing history"
              description="Your billing history will appear here once you have invoices."
              hint="Invoices are issued on each renewal and emailed to you as well."
            />
          )}
        </div>
      )}
    </div>
  );
};

export default BillingPage;
